import axios from 'axios';

import { createAuthHeaders } from '../api-client';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface GitHubConnection {
  id: string;
  workspaceId: string;
  type: string;
  name: string;
  status: string;
  metadata?: {
    login: string;
    id: number;
    avatar_url: string;
    name?: string;
    email?: string;
    hasInstallation?: boolean;
    installationAddedAt?: string;
    organizations?: Array<{
      login: string;
      id: number;
      avatar_url: string;
    }>;
  };
  createdAt: string;
  updatedAt: string;
}

export interface GitHubOrg {
  login: string;
  id: number;
  avatar_url: string;
  description?: string;
}

export interface GitHubRepo {
  id: number;
  name: string;
  full_name: string;
  private: boolean;
  html_url: string;
  description?: string;
  default_branch: string;
  owner: {
    login: string;
    avatar_url: string;
  };
}

export interface GitHubBranch {
  name: string;
  commit: {
    sha: string;
  };
  protected: boolean;
}

export interface ConnectProjectDto {
  connectionId: string;
  owner: string;
  repoName: string;
  branch?: string;
  isPrivate?: boolean;
  createRepo?: boolean;
}

export interface ImportRepoDto {
  connectionId: string;
  owner: string;
  repoName: string;
  branch?: string;
  projectName?: string;
}

export interface ImportRepoResponse {
  id: string;
  name: string;
  workspaceId: string;
}

export interface ConnectProjectResponse {
  id: string;
  githubRepoUrl: string;
  githubRepoOwner: string;
  githubRepoName: string;
  githubDefaultBranch: string;
  githubSyncEnabled: boolean;
}

export interface DisconnectProjectResponse {
  id: string;
  githubSyncEnabled: boolean;
}

export const githubApi = {
  // OAuth
  async initiateOAuth(
    token: string,
    connectionName?: string,
    projectId?: string,
    returnPath?: string
  ): Promise<{ authUrl: string }> {
    const response = await axios.post(
      `${API_URL}/api/github/oauth/initiate`,
      { connectionName, projectId, returnPath },
      { headers: createAuthHeaders(token) }
    );
    return response.data;
  },

  // Connections
  async listConnections(token: string): Promise<GitHubConnection[]> {
    const response = await axios.get(`${API_URL}/api/github/connections`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  async getConnection(token: string, connectionId: string): Promise<GitHubConnection> {
    const response = await axios.get(`${API_URL}/api/github/connections/${connectionId}`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  async deleteConnection(token: string, connectionId: string): Promise<void> {
    await axios.delete(`${API_URL}/api/github/connections/${connectionId}`, {
      headers: createAuthHeaders(token),
    });
  },

  async getManageInstallationUrl(token: string, connectionId: string): Promise<{ url: string }> {
    const response = await axios.get(
      `${API_URL}/api/github/connections/${connectionId}/manage`,
      { headers: createAuthHeaders(token) }
    );
    return response.data;
  },

  async getInstallAppUrl(token: string, connectionId: string, returnPath?: string): Promise<{ url: string }> {
    const params = new URLSearchParams();
    if (returnPath) params.append('returnPath', returnPath);

    const response = await axios.get(
      `${API_URL}/api/github/connections/${connectionId}/install-app?${params.toString()}`,
      { headers: createAuthHeaders(token) }
    );
    return response.data;
  },

  // GitHub Data
  async listOrganizations(token: string, connectionId: string): Promise<GitHubOrg[]> {
    const response = await axios.get(
      `${API_URL}/api/github/connections/${connectionId}/orgs`,
      { headers: createAuthHeaders(token) }
    );
    return response.data;
  },

  async listRepositories(
    token: string,
    connectionId: string,
    owner?: string,
    page?: number,
    perPage?: number
  ): Promise<GitHubRepo[]> {
    const params = new URLSearchParams();
    if (owner) params.append('owner', owner);
    if (page) params.append('page', String(page));
    if (perPage) params.append('per_page', String(perPage));

    const response = await axios.get(
      `${API_URL}/api/github/connections/${connectionId}/repos?${params.toString()}`,
      { headers: createAuthHeaders(token) }
    );
    return response.data;
  },

  async listBranches(
    token: string,
    connectionId: string,
    owner: string,
    repo: string
  ): Promise<GitHubBranch[]> {
    const response = await axios.get(
      `${API_URL}/api/github/connections/${connectionId}/branches?owner=${owner}&repo=${repo}`,
      { headers: createAuthHeaders(token) }
    );
    return response.data;
  },

  // Project Integration
  async connectProject(token: string, projectId: string, dto: ConnectProjectDto): Promise<ConnectProjectResponse> {
    const response = await axios.post(
      `${API_URL}/api/github/projects/${projectId}/connect`,
      dto,
      { headers: createAuthHeaders(token) }
    );
    return response.data;
  },

  async disconnectProject(token: string, projectId: string): Promise<DisconnectProjectResponse> {
    const response = await axios.post(
      `${API_URL}/api/github/projects/${projectId}/disconnect`,
      {},
      { headers: createAuthHeaders(token) }
    );
    return response.data;
  },

  async pullFromGithub(token: string, projectId: string): Promise<{ success: boolean; error?: string }> {
    const response = await axios.post(
      `${API_URL}/api/github/projects/${projectId}/pull`,
      {},
      { headers: createAuthHeaders(token) }
    );
    return response.data;
  },

  async importRepo(token: string, dto: ImportRepoDto): Promise<ImportRepoResponse> {
    const response = await axios.post(
      `${API_URL}/api/github/import`,
      dto,
      { headers: createAuthHeaders(token) }
    );
    return response.data;
  },
};
